import type {
  RawAddress, RawCart, RawCurrency, RawImage, RawOdraGarment, RawOrder, RawProduct, RawSettings,
} from './storeApiTypes';
import type {
  Address, BackPrint, Cart, Currency, FrontPrint, Garment, GarmentType, Image, Order, Product, Settings,
} from './models';
import { DEFAULT_CURRENCY, toMinor } from '@/lib/money';
import { htmlToText } from '@/lib/html';

const PICKUP_METHODS = new Set(['pickup_location', 'local_pickup']);
export const isPickupMethod = (methodId: string) => PICKUP_METHODS.has(methodId);

export function mapCurrency(c?: Partial<RawCurrency> | null): Currency {
  if (!c || !c.currency_code) return DEFAULT_CURRENCY;
  return {
    code: c.currency_code,
    symbol: c.currency_symbol ?? '',
    minorUnit: c.currency_minor_unit ?? 2,
    decimalSeparator: c.currency_decimal_separator ?? '.',
    thousandSeparator: c.currency_thousand_separator ?? ',',
    prefix: c.currency_prefix ?? '',
    suffix: c.currency_suffix ?? '',
  };
}

export function mapImage(i?: RawImage | null, fallbackAlt = ''): Image | null {
  if (!i || !i.src) return null;
  return { src: i.src, srcSet: i.srcset || undefined, sizes: i.sizes || undefined, alt: i.alt || fallbackAlt };
}

const TYPES: GarmentType[] = ['tee', 'ls', 'sweat', 'hood', 'jersey', 'casual', 'formal', 'indo', 'jodh'];
const FRONTS: FrontPrint[] = ['none', 'logo', 'gothic', 'doodle'];
const BACKS: BackPrint[] = ['none', 'number', 'small'];

/** Garment drawing settings; products without them fall back to a plain tee in a neutral colour. */
export function mapGarment(g?: RawOdraGarment | null): Garment {
  return {
    type: TYPES.includes(g?.type as GarmentType) ? (g!.type as GarmentType) : 'tee',
    color: /^#[0-9a-f]{6}$/i.test(g?.color ?? '') ? g!.color : '#e9e6dd',
    frontPrint: FRONTS.includes(g?.front_print as FrontPrint) ? (g!.front_print as FrontPrint) : 'none',
    backPrint: BACKS.includes(g?.back_print as BackPrint) ? (g!.back_print as BackPrint) : 'none',
    printText: g?.print_text ?? '',
    backNumber: g?.back_number ?? '',
  };
}

export function mapProduct(p: RawProduct): Product {
  const ext = p.extensions?.odra;
  const front = mapImage(p.images[0], p.name);
  // Back photo: the plugin's dedicated field, otherwise the second gallery image.
  const back = mapImage(ext?.images?.back, `${p.name}, back`) ?? mapImage(p.images[1], `${p.name}, back`);
  return {
    id: p.id,
    slug: p.slug,
    name: p.name,
    category: p.categories[0] ?? null,
    categorySlugs: p.categories.map((c) => c.slug),
    price: toMinor(p.prices.price),
    currency: mapCurrency(p.prices),
    colorName: ext?.color_name ?? '',
    shortDescription: htmlToText(p.short_description) || htmlToText(p.description),
    descriptionHtml: p.description,
    inStock: p.is_in_stock,
    purchasable: p.is_purchasable,
    sizeAttribute: ext?.size_attribute ?? 'pa_size',
    sizes: (ext?.sizes ?? []).map((s) => ({ slug: s.slug, name: s.name, variationId: s.variation_id, inStock: s.in_stock })),
    images: { front, back, side: mapImage(ext?.images?.side, p.name) },
    garment: mapGarment(ext?.garment),
    garmentSet: !!ext?.garment,
  };
}

function mapAddress(a?: Partial<RawAddress> | null): Address {
  return {
    firstName: a?.first_name ?? '', lastName: a?.last_name ?? '',
    address1: a?.address_1 ?? '', address2: a?.address_2 ?? '',
    city: a?.city ?? '', state: a?.state ?? '', postcode: a?.postcode ?? '',
    country: a?.country ?? '', email: a?.email ?? '', phone: a?.phone ?? '',
  };
}

export function toRawAddress(a: Address, withEmail: boolean): Partial<RawAddress> {
  const out: Partial<RawAddress> = {
    first_name: a.firstName, last_name: a.lastName, company: '',
    address_1: a.address1, address_2: a.address2, city: a.city, state: a.state,
    postcode: a.postcode, country: a.country, phone: a.phone,
  };
  if (withEmail) out.email = a.email;
  return out;
}

/**
 * Totals are shown tax-inclusive, as in the design ("Prices include VAT").
 * Store API amounts exclude tax, so the matching tax amount is added back.
 */
export function mapCart(c: RawCart): Cart {
  const t = c.totals;
  const currency = mapCurrency(t);
  return {
    lines: c.items.map((i) => {
      const ext = i.extensions?.odra;
      const size = ext?.size || i.variation.map((v) => v.value).join(' / ');
      return {
        key: i.key,
        id: i.id,
        slug: ext?.slug ?? '',
        name: i.name,
        quantity: i.quantity,
        maxQuantity: i.quantity_limits?.maximum ?? 99,
        size,
        colorName: ext?.color_name ?? '',
        unitPrice: toMinor(i.prices.price),
        lineTotal: toMinor(i.totals.line_subtotal) + toMinor(i.totals.line_subtotal_tax),
        image: mapImage(i.images[0], i.name),
        garment: mapGarment(ext?.garment),
      };
    }),
    count: c.items_count,
    subtotal: toMinor(t.total_items) + toMinor(t.total_items_tax),
    discount: toMinor(t.total_discount) + toMinor(t.total_discount_tax),
    shipping: t.total_shipping === null ? null : toMinor(t.total_shipping) + toMinor(t.total_shipping_tax),
    total: toMinor(t.total_price),
    coupons: c.coupons.map((cp) => ({
      code: cp.code.toUpperCase(),
      discount: toMinor(cp.totals.total_discount) + toMinor(cp.totals.total_discount_tax),
    })),
    rates: c.shipping_rates.flatMap((pkg) =>
      pkg.shipping_rates.map((r) => ({
        packageId: pkg.package_id,
        id: r.rate_id,
        methodId: r.method_id,
        name: r.name,
        description: htmlToText(r.description || r.delivery_time || ''),
        price: toMinor(r.price) + toMinor(r.taxes),
        selected: r.selected,
        isPickup: isPickupMethod(r.method_id),
      })),
    ),
    needsShipping: c.needs_shipping,
    paymentMethods: c.payment_methods,
    currency,
    billingAddress: mapAddress(c.billing_address),
    errors: (c.errors ?? []).map((e) => htmlToText(e.message)),
  };
}

/** How a rate is named in totals: "Courier" → "Courier delivery", "Studio pickup, Porto" → "Studio pickup". */
export function totalsLabel(r: { name: string; isPickup: boolean } | undefined): string {
  if (!r) return 'Shipping';
  if (r.isPickup) return r.name.split(',')[0];
  return /delivery|shipping/i.test(r.name) ? r.name : `${r.name} delivery`;
}

export function mapSettings(s: RawSettings): Settings {
  return {
    siteName: s.site_name || 'ODRA',
    marquee: s.marquee ?? [],
    deliveryHtml: s.delivery_html ?? '',
    freeShippingThreshold: s.free_shipping_threshold ?? null,
    pickupAddress: s.pickup_address ?? '',
    deliveryEstimates: s.delivery_estimates ?? {},
    paymentMethods: s.payment_methods ?? [],
    termsUrl: s.terms_url ?? '',
    sizeGuide: s.size_guide ?? '',
    couponHint: s.coupon_hint ?? '',
    countries: Object.entries(s.countries ?? {}).map(([code, name]) => ({ code, name: htmlToText(name) })),
    baseCountry: s.base_country ?? '',
    categories: (s.categories ?? []).map((c) => ({ id: c.id, name: htmlToText(c.name), slug: c.slug })),
    currency: mapCurrency(s.currency),
  };
}

export function mapOrder(o: RawOrder): Order {
  return {
    id: o.id,
    number: o.number,
    status: o.status,
    currency: mapCurrency(o.currency),
    items: o.items.map((i) => ({
      name: i.name, quantity: i.quantity, total: i.total, size: i.size, slug: i.slug,
      image: mapImage(i.image, i.name), garment: mapGarment(i.garment),
    })),
    totals: o.totals,
    shippingMethod: o.shipping_method,
    isPickup: o.is_pickup,
    shippingAddressLines: o.shipping_address_lines,
    pickupAddress: o.pickup_address,
    paymentMethodTitle: o.payment_method_title,
    billing: { firstName: o.billing.first_name, email: o.billing.email, phone: o.billing.phone },
    needsPayment: o.needs_payment,
  };
}
